import { ChevronDown, ChevronRight, File, Folder, Library, Search } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { ScrollableFolderPath } from '@/components/folders/ScrollableFolderPath'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { backendClient } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { Loading } from '../shared'
import Spinner from '../shared/Spinner'
import {
  type BrowseNode,
  browseOnlyHint,
  driveToNode,
  findNode,
  findPathToNode,
  folderItemsFromResponse,
  graphErrorMessage,
  isDefaultDocumentsLibrary,
  isMicrosoftResourceUnavailable,
  isSelectableSyncFolder,
  type MicrosoftFilesProviderType,
  mapTree,
  nodeCanExpand,
  ONEDRIVE_ROOT,
  ONEDRIVE_ROOT_ID,
  removeNodeById,
  siteToNode,
  toFolderItem,
  treeChildrenFromContents,
  withLoadedChildren
} from './microsoftFilesPickerUtils'
import type { FolderItem } from './WorkspacePicker'

const FOLDER_ROW_CLASS =
  'group flex w-full items-center border-b border-gray-100 px-3 py-2.5 text-left transition-all duration-150'
const FOLDER_NAME_CLASS = 'block min-w-0 truncate text-sm font-medium text-gray-700'
const LIBRARY_LABEL_CLASS = 'ml-2 shrink-0 text-xs font-normal text-gray-500'

function BrowseNodeIcon({ node, isFile }: { node: BrowseNode; isFile?: boolean }) {
  if (isFile || node.kind === 'file' || node.is_file) {
    return <File className="h-5 w-5 text-gray-400" aria-hidden />
  }
  if (node.kind === 'library') {
    return <Library className="h-5 w-5 text-blue-500/70" aria-hidden />
  }
  return <Folder className="h-5 w-5 text-amber-500/40 fill-amber-400/15" aria-hidden />
}

interface MicrosoftFilesPickerProps {
  providerType: MicrosoftFilesProviderType
  accessToken: string
  onSelect: (folder: FolderItem | null) => void
  selectedFolderId?: string
  className?: string
}

export function MicrosoftFilesPicker({
  providerType,
  accessToken,
  onSelect,
  selectedFolderId,
  className
}: MicrosoftFilesPickerProps) {
  const [treeRoots, setTreeRoots] = useState<BrowseNode[]>(
    providerType === 'onedrive' ? [ONEDRIVE_ROOT] : []
  )
  const [currentPath, setCurrentPath] = useState<BrowseNode[]>([])
  const [contents, setContents] = useState<BrowseNode[]>([])
  const [contentsNextLink, setContentsNextLink] = useState<string | null>(null)
  const [isLoadingContents, setIsLoadingContents] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => (providerType === 'onedrive' ? new Set([ONEDRIVE_ROOT_ID]) : new Set())
  )
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set())
  const [siteQuery, setSiteQuery] = useState('')
  const [debouncedSiteQuery, setDebouncedSiteQuery] = useState('')
  const hiddenIdsRef = useRef<Set<string>>(new Set())
  const libraryRootIdsRef = useRef<Map<string, string>>(new Map())

  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const treeRootsRef = useRef(treeRoots)
  treeRootsRef.current = treeRoots
  const currentPathRef = useRef(currentPath)
  currentPathRef.current = currentPath
  const browseGenerationRef = useRef(0)
  const loadMoreInFlightRef = useRef(false)
  const preserveSelectionOnMountRef = useRef(Boolean(selectedFolderId))

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSiteQuery(siteQuery), 300)
    return () => window.clearTimeout(timer)
  }, [siteQuery])

  const visibleItems = useCallback((items: BrowseNode[]) => {
    return items.filter((item) => !hiddenIdsRef.current.has(item.id))
  }, [])

  const hideNode = useCallback((nodeId: string) => {
    hiddenIdsRef.current = new Set(hiddenIdsRef.current).add(nodeId)
    setTreeRoots((prev) => removeNodeById(prev, nodeId))
  }, [])

  const notifySelection = useCallback((path: BrowseNode[]) => {
    const current = path[path.length - 1]
    if (current && isSelectableSyncFolder(current)) {
      preserveSelectionOnMountRef.current = false
      onSelectRef.current(toFolderItem(current))
      return
    }
    if (preserveSelectionOnMountRef.current) {
      preserveSelectionOnMountRef.current = false
      return
    }
    onSelectRef.current(null)
  }, [])

  const graphHeaders = useCallback(
    () => ({ 'X-OneDrive-Access-Token': accessToken }),
    [accessToken]
  )

  const fetchSites = useCallback(
    async (nextUrl?: string, search?: string) => {
      const response = await backendClient.get('/onedrive/discovery/sites', {
        params: {
          search: (search ?? '').trim() || '*',
          next_url: nextUrl
        },
        headers: graphHeaders()
      })
      return {
        items: (response.data.sites || []).map(siteToNode),
        nextLink: (response.data.next_link as string | null) || null
      }
    },
    [graphHeaders]
  )

  const fetchDrives = useCallback(
    async (siteId: string, nextUrl?: string) => {
      const response = await backendClient.get('/onedrive/discovery/drives', {
        params: {
          site_id: siteId,
          next_url: nextUrl
        },
        headers: graphHeaders()
      })
      return {
        items: (response.data.drives || []).map(driveToNode),
        nextLink: (response.data.next_link as string | null) || null
      }
    },
    [graphHeaders]
  )

  const ensureLibraryRoot = useCallback(async (node: BrowseNode): Promise<BrowseNode> => {
    if (node.kind !== 'library' || node.item_id || !node.drive_id || isDefaultDocumentsLibrary(node)) {
      return node
    }
    const cached = libraryRootIdsRef.current.get(node.drive_id)
    if (cached) {
      return { ...node, item_id: cached }
    }
    const response = await backendClient.get('/onedrive/discovery/drive-root', {
      params: { drive_id: node.drive_id },
      headers: graphHeaders()
    })
    const rootId = response.data?.root_id
    if (typeof rootId !== 'string' || rootId.length === 0) {
      return node
    }
    libraryRootIdsRef.current.set(node.drive_id, rootId)
    setTreeRoots((prev) => mapTree(prev, node.id, (current) => ({ ...current, item_id: rootId })))
    return { ...node, item_id: rootId }
  }, [graphHeaders])

  const fetchFolders = useCallback(
    async (opts: {
      parentId?: string
      driveId?: string
      parentPath?: string
      nextUrl?: string
    }) => {
      const response = await backendClient.get('/onedrive/folders', {
        params: {
          parent_id: opts.parentId,
          drive_id: opts.driveId,
          parent_path: opts.parentPath || undefined,
          include_files: true,
          source_provider: providerType,
          next_url: opts.nextUrl
        },
        headers: graphHeaders()
      })
      return {
        items: folderItemsFromResponse(response.data),
        nextLink: (response.data.next_link as string | null) || null
      }
    },
    [graphHeaders, providerType]
  )

  const loadLocationItems = useCallback(
    async (path: BrowseNode[], nextUrl?: string) => {
      const current = path[path.length - 1]
      if (providerType === 'sharepoint' && path.length === 0) {
        return fetchSites(nextUrl, debouncedSiteQuery)
      }
      if (providerType === 'onedrive' && path.length === 0) {
        return fetchFolders({ nextUrl })
      }
      if (current?.kind === 'site' && current.site_id) {
        const cached = findNode(treeRootsRef.current, current.id)
        if (cached?.children && !nextUrl) {
          return { items: cached.children, nextLink: cached.next_link ?? null }
        }
        return fetchDrives(current.site_id, nextUrl)
      }
      if (current?.kind === 'library') {
        return fetchFolders({
          driveId: current.drive_id,
          parentPath: current.path,
          nextUrl
        })
      }
      return fetchFolders({
        parentId: current?.id,
        driveId: current?.drive_id,
        parentPath: current?.path,
        nextUrl
      })
    },
    [debouncedSiteQuery, fetchDrives, fetchFolders, fetchSites, providerType]
  )

  const attachTreeChildren = useCallback((
    parentId: string | null,
    items: BrowseNode[],
    append: boolean,
    nextLink: string | null
  ) => {
    const folderItems = treeChildrenFromContents(items)
    if (parentId === null) {
      if (providerType === 'sharepoint') {
        setTreeRoots((prev) => (append ? [...prev, ...folderItems] : folderItems))
        return
      }
      setTreeRoots((prev) =>
        mapTree(prev, ONEDRIVE_ROOT_ID, (node) => (
          withLoadedChildren(
            node,
            append ? [...(node.children || []), ...folderItems] : folderItems,
            nextLink
          )
        ))
      )
      return
    }
    setTreeRoots((prev) =>
      mapTree(prev, parentId, (node) => (
        withLoadedChildren(
          node,
          append ? [...(node.children || []), ...folderItems] : folderItems,
          nextLink
        )
      ))
    )
    if (folderItems.length > 0 || nextLink) {
      setExpandedIds((prev) => new Set(prev).add(parentId))
    }
  }, [providerType])

  const navigateTo = useCallback(
    async (path: BrowseNode[]) => {
      const generation = browseGenerationRef.current + 1
      browseGenerationRef.current = generation
      setIsLoadingContents(true)
      setIsLoadingMore(false)
      setError(null)
      setCurrentPath(path)
      notifySelection(path)
      let handedOff = false
      const isCurrent = () => generation === browseGenerationRef.current
      let resolvedPath = path
      const target = path[path.length - 1]
      if (
        target?.kind === 'library' &&
        !target.item_id &&
        target.drive_id &&
        !isDefaultDocumentsLibrary(target)
      ) {
        try {
          const withRoot = await ensureLibraryRoot(target)
          if (!isCurrent()) {
            return
          }
          resolvedPath = [...path.slice(0, -1), withRoot]
          setCurrentPath(resolvedPath)
          notifySelection(resolvedPath)
        } catch (err) {
          if (!isCurrent()) {
            return
          }
          const message = graphErrorMessage(err, 'Failed to open this library')
          toast.error(message)
          setError(message)
          setContents([])
          setContentsNextLink(null)
          setIsLoadingContents(false)
          return
        }
      }
      try {
        const { items, nextLink } = await loadLocationItems(resolvedPath)
        if (!isCurrent()) {
          return
        }
        const visible = visibleItems(items)
        setContents(visible)
        setContentsNextLink(nextLink)
        const parentId = resolvedPath[resolvedPath.length - 1]?.id ?? (providerType === 'onedrive' ? ONEDRIVE_ROOT_ID : null)
        attachTreeChildren(parentId, visible, false, nextLink)
      } catch (err) {
        if (!isCurrent()) {
          return
        }
        if (isMicrosoftResourceUnavailable(err) && resolvedPath.length > 0) {
          const deniedId = resolvedPath[resolvedPath.length - 1].id
          handedOff = true
          hideNode(deniedId)
          void navigateToRef.current(resolvedPath.slice(0, -1))
          return
        }
        setError(graphErrorMessage(err, 'Failed to load folders'))
        setContents([])
        setContentsNextLink(null)
      } finally {
        if (!handedOff && isCurrent()) {
          setIsLoadingContents(false)
        }
      }
    },
    [attachTreeChildren, ensureLibraryRoot, loadLocationItems, notifySelection, providerType, visibleItems, hideNode]
  )

  const navigateToRef = useRef(navigateTo)
  navigateToRef.current = navigateTo
  const siteSearchKey = providerType === 'sharepoint' ? debouncedSiteQuery : ''

  useEffect(() => {
    if (!accessToken) {
      return
    }
    hiddenIdsRef.current = new Set()
    libraryRootIdsRef.current = new Map()
    setTreeRoots(providerType === 'onedrive' ? [ONEDRIVE_ROOT] : [])
    setExpandedIds(providerType === 'onedrive' ? new Set([ONEDRIVE_ROOT_ID]) : new Set())
    setCurrentPath([])
    setContents([])
    setContentsNextLink(null)
    void siteSearchKey
    void navigateToRef.current([])
  }, [accessToken, providerType, siteSearchKey])

  const loadMore = async () => {
    if (!contentsNextLink || loadMoreInFlightRef.current) {
      return
    }
    loadMoreInFlightRef.current = true
    const generation = browseGenerationRef.current
    const path = currentPath
    const pageUrl = contentsNextLink
    setIsLoadingMore(true)
    setError(null)
    try {
      const { items, nextLink } = await loadLocationItems(path, pageUrl)
      if (generation !== browseGenerationRef.current) {
        return
      }
      const visible = visibleItems(items)
      setContents((prev) => [...prev, ...visible])
      setContentsNextLink(nextLink)
      const parentId = path[path.length - 1]?.id ?? (providerType === 'onedrive' ? ONEDRIVE_ROOT_ID : null)
      attachTreeChildren(parentId, visible, true, nextLink)
    } catch (err) {
      if (generation !== browseGenerationRef.current) {
        return
      }
      setError(graphErrorMessage(err, 'Failed to load more items'))
    } finally {
      loadMoreInFlightRef.current = false
      if (generation === browseGenerationRef.current) {
        setIsLoadingMore(false)
      }
    }
  }

  const loadChildrenForTree = async (node: BrowseNode): Promise<{ items: BrowseNode[]; nextLink: string | null }> => {
    if (node.kind === 'root') {
      const { items, nextLink } = await fetchFolders({})
      return { items: treeChildrenFromContents(items), nextLink }
    }
    if (node.kind === 'site' && node.site_id) {
      return fetchDrives(node.site_id)
    }
    if (node.kind === 'library') {
      const { items, nextLink } = await fetchFolders({
        driveId: node.drive_id,
        parentPath: node.path
      })
      return { items: treeChildrenFromContents(items), nextLink }
    }
    const { items, nextLink } = await fetchFolders({
      parentId: node.id,
      driveId: node.drive_id,
      parentPath: node.path
    })
    return { items: treeChildrenFromContents(items), nextLink }
  }

  const toggleExpand = async (node: BrowseNode) => {
    if (expandedIds.has(node.id)) {
      setExpandedIds((prev) => {
        const next = new Set(prev)
        next.delete(node.id)
        return next
      })
      return
    }
    if (!node.children) {
      setLoadingIds((prev) => new Set(prev).add(node.id))
      try {
        const { items, nextLink } = await loadChildrenForTree(node)
        const loaded = withLoadedChildren(node, items, nextLink)
        setTreeRoots((prev) =>
          mapTree(prev, node.id, () => loaded)
        )
        if (!nodeCanExpand(loaded)) {
          return
        }
      } catch (err) {
        if (isMicrosoftResourceUnavailable(err)) {
          hideNode(node.id)
          setContents((prev) => prev.filter((item) => item.id !== node.id))
          return
        }
        setError(graphErrorMessage(err, 'Failed to load folders'))
        return
      } finally {
        setLoadingIds((prev) => {
          const next = new Set(prev)
          next.delete(node.id)
          return next
        })
      }
    }
    setExpandedIds((prev) => new Set(prev).add(node.id))
  }

  const handleTreeNodeClick = (node: BrowseNode) => {
    if (node.kind === 'root') {
      void navigateTo([])
      return
    }
    const path = findPathToNode(treeRootsRef.current, node.id)
    if (!path) {
      void navigateTo([node])
      return
    }
    void navigateTo(path.filter((item) => item.kind !== 'root'))
  }

  const handleContentsClick = (node: BrowseNode) => {
    if (node.kind === 'file' || node.is_file) {
      return
    }
    void navigateTo([...currentPathRef.current, node])
  }

  const handleBreadcrumbClick = (index: number) => {
    if (index < 0) {
      void navigateTo([])
      return
    }
    void navigateTo(currentPath.slice(0, index + 1))
  }

  const currentNode = currentPath[currentPath.length - 1]
  const currentIsSelectable = Boolean(currentNode && isSelectableSyncFolder(currentNode))
  const currentHint = browseOnlyHint(currentNode, providerType)
  const rootLabel = providerType === 'sharepoint' ? 'Sites' : 'My files'
  const selectedTreeId = currentNode?.id ?? (providerType === 'onedrive' ? ONEDRIVE_ROOT_ID : null)

  const renderTreeNode = (node: BrowseNode, depth: number): ReactNode => {
    const isExpanded = expandedIds.has(node.id)
    const isLoading = loadingIds.has(node.id)
    const isCurrent = selectedTreeId === node.id
    const canExpand = nodeCanExpand(node)
    const paddingLeft = depth * 14 + 8

    return (
      <div key={node.id} className="select-none">
        <div
          className={cn(
            FOLDER_ROW_CLASS,
            isCurrent ? 'bg-blue-50 border-blue-200' : 'cursor-pointer hover:bg-gray-50'
          )}
          style={{ paddingLeft: `${paddingLeft}px` }}
        >
          <div className="mr-1.5 flex h-5 w-5 items-center justify-center">
            {canExpand ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  void toggleExpand(node)
                }}
                className="flex h-5 w-5 items-center justify-center rounded-xs transition-colors duration-150 hover:bg-gray-200"
                disabled={isLoading}
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.name}`}
                aria-expanded={isExpanded}
              >
                {isLoading ? (
                  <Spinner size="sm" />
                ) : isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-600" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-600" />
                )}
              </button>
            ) : (
              <div className="h-5 w-5" />
            )}
          </div>
          <button
            type="button"
            onClick={() => handleTreeNodeClick(node)}
            className="flex min-w-0 flex-1 items-center text-left"
          >
            <div className="mr-3 flex h-5 w-5 items-center justify-center">
              <BrowseNodeIcon node={node} />
            </div>
            <span className={FOLDER_NAME_CLASS}>{node.name}</span>
            {node.kind === 'library' ? (
              <span className={LIBRARY_LABEL_CLASS}>Library</span>
            ) : null}
          </button>
        </div>
        {isExpanded ? node.children?.map((child) => renderTreeNode(child, depth + 1)) : null}
      </div>
    )
  }

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      <div className="flex min-h-0 flex-1">
        <nav
          aria-label="Folder tree"
          className="flex w-72 shrink-0 flex-col border-r border-gray-200 bg-gray-50"
        >
          {providerType === 'sharepoint' && (
            <div className="border-b border-gray-200 p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={siteQuery}
                  onChange={(event) => setSiteQuery(event.target.value)}
                  placeholder="Search sites"
                  className="h-8 pl-8"
                  aria-label="Search sites"
                />
              </div>
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto py-2">
            {treeRoots.length === 0 && isLoadingContents ? (
              <div className="flex items-center justify-center py-8">
                <Loading text="Loading folders" />
              </div>
            ) : (
              treeRoots.map((node) => renderTreeNode(node, 0))
            )}
          </div>
        </nav>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-2">
            <nav aria-label="Breadcrumb" className="min-w-0 text-sm">
              <ScrollableFolderPath>
                <button
                  type="button"
                  onClick={() => handleBreadcrumbClick(-1)}
                  className={cn(
                    'shrink-0 hover:text-blue-600 hover:underline',
                    currentPath.length === 0 ? 'font-medium text-gray-900' : 'text-gray-600'
                  )}
                >
                  {rootLabel}
                </button>
                {currentPath.map((node, index) => (
                  <span key={node.id} className="contents">
                    <span className="mx-1.5 shrink-0 text-gray-400" aria-hidden="true">/</span>
                    <button
                      type="button"
                      onClick={() => handleBreadcrumbClick(index)}
                      className={cn(
                        'shrink-0 hover:text-blue-600 hover:underline',
                        index === currentPath.length - 1 ? 'font-medium text-gray-900' : 'text-gray-600'
                      )}
                    >
                      {node.name}
                    </button>
                  </span>
                ))}
              </ScrollableFolderPath>
            </nav>
          </div>

          <section aria-label="Folder contents" className="min-h-0 flex-1 overflow-y-auto">
            {isLoadingContents ? (
              <div className="flex h-32 items-center justify-center">
                <Loading text="Loading folders" />
              </div>
            ) : error ? (
              <div className="p-4 text-center text-sm text-red-600">
                {error}
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => void navigateTo(currentPath)}
                  text="Retry"
                />
              </div>
            ) : contents.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                {providerType === 'sharepoint' && currentPath.length === 0
                  ? 'No SharePoint sites found'
                  : currentNode?.kind === 'site'
                    ? 'No libraries in this site'
                    : 'No items in this folder'}
              </div>
            ) : (
              <div>
                {contents.map((node) => {
                  const isFile = node.kind === 'file' || Boolean(node.is_file)
                  return (
                    <button
                      key={node.id}
                      type="button"
                      disabled={isFile}
                      onClick={() => handleContentsClick(node)}
                      className={cn(
                        FOLDER_ROW_CLASS,
                        isFile
                          ? 'cursor-default opacity-50'
                          : 'cursor-pointer hover:bg-gray-50'
                      )}
                      title={isFile ? 'Files cannot be selected for sync' : undefined}
                    >
                      <div className="mr-1.5 h-5 w-5" />
                      <div className="mr-3 flex h-5 w-5 items-center justify-center">
                        <BrowseNodeIcon node={node} isFile={isFile} />
                      </div>
                      <span className={cn(FOLDER_NAME_CLASS, isFile && 'text-gray-500')}>
                        {node.name}
                      </span>
                      {node.kind === 'library' ? (
                        <span className={LIBRARY_LABEL_CLASS}>Library</span>
                      ) : null}
                    </button>
                  )
                })}
                {contentsNextLink ? (
                  <div className="px-3 py-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void loadMore()}
                      loading={isLoadingMore}
                      loadingText="Loading"
                      text="Load more"
                    />
                  </div>
                ) : null}
              </div>
            )}
          </section>
        </div>
      </div>

      <div
        className={cn(
          'shrink-0 border-t px-4 py-3 text-xs',
          currentIsSelectable ? 'bg-gray-50 text-gray-600' : 'border-amber-100 bg-amber-50 text-amber-800'
        )}
      >
        {currentIsSelectable
          ? `This folder will be synced: ${currentNode?.name}. Click Next to continue.`
          : currentHint}
      </div>
    </div>
  )
}
